import type { MetroLanguage, MetroLanguageOption } from '../types/metro';

export interface MetroCityCatalogEntry {
  id: string;
  name: string;
  localName: string;
  country: string;
  localizedNames?: Record<MetroLanguage, string>;
  localizedCountries?: Record<MetroLanguage, string>;
  languages: readonly MetroLanguageOption[];
}

/**
 * Lightweight city metadata used by the landing page.
 *
 * IMPORTANT: Never import full city configs/data here. Keeping this module small ensures
 * topology, geometry, aliases and translations are loaded only when a city route is opened.
 */
export const metroCities: readonly MetroCityCatalogEntry[] = [
  {
    id: 'bucharest',
    name: 'Bucharest',
    localName: 'București',
    country: 'Romania',
    localizedNames: { en: 'Bucharest', ro: 'București' },
    localizedCountries: { en: 'Romania', ro: 'România' },
    languages: [{ code: 'ro', label: 'RO', nativeName: 'Română' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'paris',
    name: 'Paris',
    localName: 'Paris',
    country: 'France',
    localizedNames: { en: 'Paris', fr: 'Paris' },
    localizedCountries: { en: 'France', fr: 'France' },
    languages: [{ code: 'fr', label: 'FR', nativeName: 'Français' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'copenhagen',
    name: 'Copenhagen',
    localName: 'København',
    country: 'Denmark',
    localizedNames: { en: 'Copenhagen', da: 'København' },
    localizedCountries: { en: 'Denmark', da: 'Danmark' },
    languages: [{ code: 'da', label: 'DA', nativeName: 'Dansk' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'stockholm',
    name: 'Stockholm',
    localName: 'Stockholm',
    country: 'Sweden',
    localizedNames: { en: 'Stockholm', sv: 'Stockholm' },
    localizedCountries: { en: 'Sweden', sv: 'Sverige' },
    languages: [{ code: 'sv', label: 'SV', nativeName: 'Svenska' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'prague',
    name: 'Prague',
    localName: 'Praha',
    country: 'Czechia',
    localizedNames: { en: 'Prague', cs: 'Praha' },
    localizedCountries: { en: 'Czechia', cs: 'Česko' },
    languages: [{ code: 'cs', label: 'CS', nativeName: 'Čeština' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'warsaw',
    name: 'Warsaw',
    localName: 'Warszawa',
    country: 'Poland',
    localizedNames: { en: 'Warsaw', pl: 'Warszawa' },
    localizedCountries: { en: 'Poland', pl: 'Polska' },
    languages: [{ code: 'pl', label: 'PL', nativeName: 'Polski' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'budapest',
    name: 'Budapest',
    localName: 'Budapest',
    country: 'Hungary',
    localizedNames: { en: 'Budapest', hu: 'Budapest' },
    localizedCountries: { en: 'Hungary', hu: 'Magyarország' },
    languages: [{ code: 'hu', label: 'HU', nativeName: 'Magyar' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  },
  {
    id: 'sofia',
    name: 'Sofia',
    localName: 'София',
    country: 'Bulgaria',
    localizedNames: { en: 'Sofia', bg: 'София' },
    localizedCountries: { en: 'Bulgaria', bg: 'България' },
    languages: [{ code: 'bg', label: 'BG', nativeName: 'Български' }, { code: 'en', label: 'EN', nativeName: 'English' }]
  }
] as const;


export const homeLanguages: readonly MetroLanguageOption[] = Array.from(
  new Map(
    metroCities
      .flatMap(city => city.languages)
      .map(option => [option.code, option] as const)
  ).values()
).sort((a, b) => {
  if (a.code === 'en') return -1;
  if (b.code === 'en') return 1;
  return a.nativeName.localeCompare(b.nativeName);
});
